
import React from 'react';

import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/themes/prism-tomorrow.css"; // Syntax highlighting theme

const highlightCode = (code: string) =>
   Prism.highlight(code, Prism.languages.html, "html");

type HtmlPrettyPrintProps = {
   label: string,
   value?: string | null,
   onValueChanged?: (name: string, value?: string | null) => void;
};

const EnhancedTextArea: React.FC<HtmlPrettyPrintProps> = ({ label, value, onValueChanged }) => {
   const inputRef = React.useRef<HTMLTextAreaElement | HTMLInputElement>(null);

   const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
      const clipboardData = event.clipboardData;
      const items = clipboardData?.items;
      const oldValue = value ?? '';
      if (!items) return;

      for (let ii = 0; ii < items.length; ii++) {
         const item = items[ii];
         if (item.type.indexOf('image') !== -1) {
            const blob = item.getAsFile();
            if (blob) {
               const reader = new FileReader();
               reader.onload = (e: ProgressEvent<FileReader>) => {
                  const dataUrl = e.target?.result as string;
                  const imgTag = `<img src="${dataUrl}" />`;

                  // Get the current cursor (selection) position.
                  const input = inputRef.current;
                  if (input) {
                     const startPos = input.selectionStart || 0;
                     const endPos = input.selectionEnd || 0;
                     const textBefore = oldValue.substring(0, startPos);
                     const textAfter = oldValue.substring(endPos);
                     const newValue = textBefore + imgTag + textAfter;

                     if (onValueChanged) {
                        onValueChanged(label, newValue);
                     }

                     // Optionally, set the cursor position after the inserted tag.
                     setTimeout(() => {
                        if (input) {
                           const pos = startPos + imgTag.length;
                           input.setSelectionRange(pos, pos);
                        }
                     }, 0);
                  } else {
                     // Fallback if ref is not available.
                     if (onValueChanged) {
                        onValueChanged(label, oldValue + imgTag);
                     }
                  }
               };
               reader.readAsDataURL(blob);
            }
            // Prevent the default paste behavior.
            event.preventDefault();
            break; // Optionally handle only one image.
         }
      }
   };

   const handleChange = React.useCallback((update: string) => {
      if (onValueChanged) {
         onValueChanged(label, update);
      }
   }, [label, onValueChanged]);

   return (
      <Editor
         value={value ?? ''}
         onValueChange={handleChange}
         highlight={highlightCode}
         padding={10}
         onPaste={handlePaste}
         style={{
            fontFamily: '"Fira code", "Fira Mono", monospace',
            fontSize: 16,
            maxWidth: '80vw',
            backgroundColor: "#2d2d2d",
            color: "white"
         }}
      />
   );
};

export default EnhancedTextArea;
